import { capitalCase } from "change-case";
import { useEffect, useState } from "react";

import { sectionFallback, useToast } from "@smart/bridge-intake-components-dom";
import { TField } from "@smart/bridge-resources-basic";
import {
  defaultClientRoleId,
  roleProviderId,
} from "@smart/bridge-smokeball-basic";
import { buildMessageTemplate } from "@smart/bridge-templates-basic";
import {
  FieldNS,
  Country,
  SectionNS,
  SubmissionLinkHashNS,
  UserNS,
  AnalyticsTool,
  isDigitalSignatureField,
} from "@smart/bridge-types-basic";
import { loadLocale } from "@smart/itops-locale-dom";
import { Alert, Button, Link } from "@smart/itops-sb-design-system-dom";
import { extractId } from "@smart/itops-types-basic";
import { rankBetween, toSubdomain } from "@smart/itops-utils-basic";
import {
  useLazyLoadFields,
  useLazyLoadMatterTypes,
  useUpdateField,
  useUpdateFormPublishStatus,
  useUpdateSection,
  useDeleteSubmissionLinkHash,
  useUpdateSubmissionLinkHash,
} from "@smart/manage-gql-client-dom";

import { InfoAlert } from "./info-alert";
import { SubmissionLinkHashesTable } from "./submission-link-hashes-table";
import { GenerateQRCode } from "../../../shared";
import {
  findClientRoles,
  validateForm,
} from "../../../shared/validation-utils";
import {
  DeleteWebEnquiryLinkModal,
  WebEnquiryLinkModal,
  type WebEnquiryLinkFormValues,
} from "../../shared";
import type { EmbedType } from "../../types";
import { ContentCard } from "../heading/settings/content-card";
import {
  GqlForm,
  GqlMatterLayout,
  GqlSection,
  GqlSubmissionLinkHash,
} from "../types";

type ValidationStatus = "valid" | "invalid" | "validating";
type WebEnquiriesTabProps = {
  form?: GqlForm | null;
  sections?: GqlSection[] | null;
  matterLayouts?: GqlMatterLayout[];
  toggleFormActive: () => Promise<void>;
  submissionLinkHashes?: GqlSubmissionLinkHash[];
};

export const WebEnquiriesTab = ({
  form,

  sections,
  matterLayouts,
  toggleFormActive,
  submissionLinkHashes,
}: WebEnquiriesTabProps) => {
  const { country, terms } = loadLocale();
  const loadFields = useLazyLoadFields();
  const [loadMatterTypes] = useLazyLoadMatterTypes();
  const [updateField] = useUpdateField();
  const [updateSection] = useUpdateSection();
  const [updateFormPublishStatus] = useUpdateFormPublishStatus();
  const [validationStatus, setValidationStatus] = useState<
    ValidationStatus | undefined
  >();
  const [hasDigitalSignatureField, setHasDigitalSignatureField] =
    useState(false);
  const clientRole =
    matterLayouts?.find((l) => l.providerId === roleProviderId)?.name ||
    "Client";
  const { setToasts } = useToast();
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [editingSubmissionLinkHash, setEditingSubmissionLinkHash] = useState<
    GqlSubmissionLinkHash | undefined
  >();
  const [deletingSubmissionLinkHash, setDeletingSubmissionLinkHash] = useState<
    GqlSubmissionLinkHash | undefined
  >();
  const [updateSubmissionLinkHash, { loading: savingLink }] =
    useUpdateSubmissionLinkHash();
  const [deleteSubmissionLinkHash, { loading: deletingLink }] =
    useDeleteSubmissionLinkHash();
  const [generatingQRCode, setGeneratingQRCode] = useState<string>();

  const formLinks = submissionLinkHashes?.filter((link) => !link.deleted) || [];
  const closeLinkModal = () => {
    setLinkModalOpen(false);
    setEditingSubmissionLinkHash(undefined);
  };

  const closeDeleteModal = () => setDeletingSubmissionLinkHash(undefined);

  const linkUrl: Record<Country, string> = {
    AU: "https://support.smokeball.com/hc/en-au/articles/8015208933911",
    US: "https://support.smokeball.com/hc/en-us/articles/8015208933911",
    GB: "https://support.smokeball.com/hc/en-gb/articles/8015208933911",
  };

  const loadFormData = async () => {
    if (!form) return { fields: [], clientRoleIds: [] };
    try {
      const fields = (await loadFields({ formUri: form.uri }))?.map(
        (f) => f.values as unknown as TField,
      );
      const matterTypes = await loadMatterTypes({
        variables: {
          type: form.formCategory,
          matterTypeIds: form.rawForm.values.matterTypes.map((mt) => mt.id),
        },
      });
      const roles = findClientRoles(matterTypes.data?.matterTypes);
      const clientRoleIds = [defaultClientRoleId, ...roles];
      return { fields, clientRoleIds };
    } catch (error) {
      console.error("Error loading form data", error);
      return { fields: [], clientRoleIds: [] };
    }
  };

  const validate = async () => {
    if (!form) return setValidationStatus("invalid");
    try {
      setValidationStatus("validating");
      const { fields, clientRoleIds } = await loadFormData();

      setHasDigitalSignatureField(
        !!fields?.some((f) => isDigitalSignatureField(f)),
      );

      if (!fields?.length) return setValidationStatus("invalid");

      if (clientRoleIds.some((role) => validateForm(fields, role)))
        return setValidationStatus("valid");

      return setValidationStatus("invalid");
    } catch (error) {
      console.error("Error validating form", error);
      return setValidationStatus("invalid");
    }
  };

  const setupNameFields = async () => {
    if (!form) return;

    try {
      const { fields, clientRoleIds } = await loadFormData();
      let changedForm = false;

      const clientFieldNames = [
        "person/firstName",
        "person/lastName",
        "company/name",
        "trust/name",
      ];

      const existingClientFields = fields?.filter(
        (f) =>
          clientRoleIds.includes(f.layout?.id || "") &&
          clientFieldNames.includes(f.field?.name || ""),
      );

      if (existingClientFields?.length) {
        // Mark all non-mandatory client fields as mandatory
        for (const existing of existingClientFields) {
          // eslint-disable-next-line no-continue
          if (existing.mandatory) continue;
          await updateField({
            variables: {
              uri: existing.uri,
              formUri: form.uri,
              sectionUri: existing.sectionUri,
              groupUri: existing.groupUri || undefined,
              order: existing.order,
              fields: {
                type: existing.type,
                label: existing.label,
                hint: existing.hint || "",
                options: (existing.options || []).map((o) => ({
                  label: o.label,
                  value: o.value,
                })),
                mandatory: true,
                layout: {
                  id: existing.layout!.id,
                  name: existing.layout!.name,
                  providerId: existing.layout!.providerId,
                  parentId: existing.layout!.parentId || undefined,
                  parentName: existing.layout!.parentName || undefined,
                  parentProviderId:
                    existing.layout!.parentProviderId || undefined,
                },
                field: {
                  name: existing.field!.name,
                  type: existing.field!.type,
                  possibleValues: existing.field!.possibleValues || undefined,
                  dataSourceMapping:
                    existing.field!.dataSourceMapping || undefined,
                  originalName: existing.field!.originalName || undefined,
                },
              },
            },
          });
          changedForm = true;
        }
      } else {
        // No client fields exist, add person name fields
        let sectionUri = sections?.[0]?.uri;

        if (!sectionUri) {
          sectionUri = SectionNS.generateUri();
          await updateSection({
            variables: {
              uri: sectionUri,
              formUri: form.uri,
              order: rankBetween({}),
              fields: {
                title: sectionFallback,
              },
            },
          });
        }

        const personFieldDefs = [
          { name: "person/lastName", label: "Last Name" },
          { name: "person/firstName", label: "First Name" },
        ];

        const roleLayout = matterLayouts?.find(
          (l) => l.providerId === roleProviderId,
        );

        let orderBefore = fields?.[0]?.order;
        for (const { name, label } of personFieldDefs) {
          const order = rankBetween({ before: orderBefore });
          orderBefore = order;

          await updateField({
            variables: {
              uri: FieldNS.generateUri(),
              formUri: form.uri,
              sectionUri,
              order,
              fields: {
                type: "text",
                label,
                hint: "",
                options: [],
                mandatory: true,
                supportsCustomValues: true,
                layout: {
                  id: defaultClientRoleId,
                  name: roleLayout?.name || defaultClientRoleId,
                  providerId: roleProviderId,
                },
                field: {
                  name,
                  type: "Text",
                  possibleValues: [],
                  originalName: name,
                },
              },
            },
          });
          changedForm = true;
        }
      }

      if (changedForm) {
        await updateFormPublishStatus({
          variables: { uri: form.uri, hasUnpublishedChanges: true },
        });
      }

      await validate();
    } catch (error) {
      console.error("Error setting up name fields", error);
      setValidationStatus("invalid");
    }
  };

  useEffect(() => {
    if (!form) return;
    validate().catch(console.error);
  }, [form]);

  const onSubmitWebEnquiryLink = async ({
    uri,
    values,
  }: {
    uri?: string;
    values: WebEnquiryLinkFormValues;
  }) => {
    if (!form) return;

    const analyticsTools: AnalyticsTool[] | undefined =
      values.gaMeasurementId.trim().length > 0
        ? [
            {
              type: "googleAnalytics",
              trackingId: values.gaMeasurementId.trim(),
            },
          ]
        : undefined;

    try {
      await updateSubmissionLinkHash({
        variables: {
          uri: uri ?? SubmissionLinkHashNS.generateUri(),
          fields: {
            formUri: form.uri,
            slugUri: form.rawForm.slug?.uri ?? "",
            label: values.linkLabel.trim(),
            notifiedUserUris: values.staffIds.map((id: string) =>
              UserNS.generateUri("sb", id),
            ),
            analyticsTools,
          },
        },
      });
      setToasts([
        {
          text: uri ? "Link and QR code updated" : "Link and QR code created",
          width: "medium",
        },
      ]);
      closeLinkModal();
    } catch (error) {
      console.error(error);
      setToasts([
        {
          text: uri
            ? "Failed to update link and QR code"
            : "Failed to create link and QR code",
          palette: "destructive",
          width: "medium",
        },
      ]);
    }
  };

  const confirmDeleteLink = async () => {
    if (!deletingSubmissionLinkHash) return;

    const { uri } = deletingSubmissionLinkHash;

    try {
      await deleteSubmissionLinkHash({ variables: { uri } });
      closeDeleteModal();
      setToasts([
        { text: "Link deleted", palette: "default", width: "medium" },
      ]);
    } catch (error) {
      console.error(error);
      setToasts([
        {
          text: `Failed to delete web ${terms.enquiry.name} link`,
          palette: "destructive",
          width: "medium",
        },
      ]);
      closeDeleteModal();
    }
  };

  const onViewQRCode = (linkHash: GqlSubmissionLinkHash) => {
    const link = toSubdomain(undefined, `/create/${extractId(linkHash.uri)}`);
    setGeneratingQRCode(link);
  };

  const onCopyLink = (linkHash: GqlSubmissionLinkHash, type: EmbedType) => {
    const link = toSubdomain(undefined, `/create/${extractId(linkHash.uri)}`);

    const code =
      type === "url"
        ? link
        : buildMessageTemplate({
            link,
            format: "embed",
            category: "lead",
            matterType: { name: "" },
            form: { title: "" },
            embedOption: type,
          }).html;

    navigator.clipboard
      .writeText(code)
      .then(() =>
        setToasts([
          {
            text: `${type === "url" ? "Link" : "Code"} copied and ready to be used`,
          },
        ]),
      )
      .catch(console.error);
  };

  return (
    <div className="flex flex-grow bg-gray-100 justify-center p-[2rem]">
      <div className="flex w-fit h-fit">
        <ContentCard
          title={`Web ${capitalCase(terms.enquiry.plural)} Links and QR Codes`}
          headerRight={
            <Button
              text="Create Link and QR Code"
              variant="primaryDefault"
              size="small"
              onClick={() => {
                setEditingSubmissionLinkHash(undefined);
                setLinkModalOpen(true);
              }}
              disabled={
                !form?.active ||
                validationStatus !== "valid" ||
                hasDigitalSignatureField
              }
            />
          }
        >
          <div className="flex flex-col gap-medium w-[91.2rem] h-[62.2rem] py-[1.6rem] px-[2.4rem]">
            <div className="flex gap-small">
              <p className="text-ui">
                Share this form across different platforms and track where{" "}
                {terms.enquiry.plural} come from.
              </p>
              <Link text="Learn More" href={linkUrl[country]} target="_blank" />
            </div>
            {form?.rawForm.values.hasUnpublishedChanges && (
              <Alert
                variant="section"
                palette="default"
                content={
                  <p>
                    This form has unpublished changes. The link and QR code
                    share the last published version. Publish first, then create
                    the link or QR code to share your latest edits.
                  </p>
                }
              />
            )}
            <InfoAlert
              isFormActive={!!form?.active}
              hasRequiredField={validationStatus === "valid"}
              clientRole={clientRole}
              toggleFormActive={toggleFormActive}
              setupNameFields={setupNameFields}
              hasFormLinks={!!formLinks.length}
              hasDigitalSignatureField={hasDigitalSignatureField}
            />
            {form && (
              <SubmissionLinkHashesTable
                formTitle={form.formTitle}
                formLinks={formLinks}
                onEditLink={(submissionLinkHash) => {
                  setEditingSubmissionLinkHash(submissionLinkHash);
                  setLinkModalOpen(true);
                }}
                onDeleteLink={setDeletingSubmissionLinkHash}
                onCopyLink={onCopyLink}
                onViewQRCode={onViewQRCode}
              />
            )}
          </div>
        </ContentCard>
      </div>
      <WebEnquiryLinkModal
        open={linkModalOpen}
        onClose={closeLinkModal}
        formTitle={form?.formTitle || "Untitled Form"}
        submissionLinkHash={editingSubmissionLinkHash}
        loading={savingLink}
        onSubmit={onSubmitWebEnquiryLink}
      />
      <DeleteWebEnquiryLinkModal
        open={!!deletingSubmissionLinkHash}
        loading={deletingLink}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteLink}
      />
      <GenerateQRCode
        link={generatingQRCode}
        onClose={() => setGeneratingQRCode(undefined)}
        onCopied={() =>
          setToasts([{ text: "QR code copied", width: "medium" }])
        }
        onDownloaded={() =>
          setToasts([{ text: "QR code downloaded", width: "medium" }])
        }
      />
    </div>
  );
};
